import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// ---------------------------------------------------------------------------
// SAMPLE DATA — matches the shape this component expects. Handy for demos /
// Storybook; not used internally. Pass your real payload via the `data` prop.
// ---------------------------------------------------------------------------
export const sampleData = {
  label_a: "Without BESS",
  label_b: "With BESS",
  summary: {
    cheaper: "With BESS",
    total_a: 17917.208742500003,
    total_b: 12696.25906,
    pct_change: -29.13930265329665,
    total_diff: -5220.949682500002,
  },
  components: {
    fixed_charge: { diff: 0.0, value_a: 345.3204, value_b: 345.3204, pct_change: 0.0 },
    energy_charge: { diff: 137.24831749999976, value_a: 4859.2463425000005, value_b: 4996.49466, pct_change: 2.824477456505895 },
    tou_demand_charge: { diff: -879.6000000000001, value_a: 2035.24, value_b: 1155.64, pct_change: -43.218490202629674 },
    flat_demand_charge: { diff: -4478.598, value_a: 10677.402, value_b: 6198.804, pct_change: -41.944641589779984 },
    total_estimated_bill: { diff: -5220.949682500002, value_a: 17917.208742500003, value_b: 12696.25906, pct_change: -29.13930265329665 },
  },
  period_note: null,
  same_billing_period: true,
  demand_period_comparison: {
    period_0: { charge_a: 0.0, charge_b: 0.0, in_bill_a: true, in_bill_b: true, peak_kw_a: 281.8, peak_kw_b: 129.2, applies_on: "every day", charge_diff: 0.0, peak_kw_diff: -152.60000000000002, weekday_hours: ["00:00-14:00", "23:00-24:00"], weekend_hours: ["00:00-14:00", "23:00-24:00"] },
    period_2: { charge_a: 299.44, charge_b: 310.84, in_bill_a: true, in_bill_b: true, peak_kw_a: 157.6, peak_kw_b: 163.6, applies_on: "every day", charge_diff: 11.399999999999975, peak_kw_diff: 6.0, weekday_hours: ["14:00-16:00", "21:00-23:00"], weekend_hours: ["14:00-16:00", "21:00-23:00"] },
    period_3: { charge_a: 1735.8, charge_b: 844.8, in_bill_a: true, in_bill_b: true, peak_kw_a: 263.0, peak_kw_b: 128.0, applies_on: "every day", charge_diff: -891.0, peak_kw_diff: -135.0, weekday_hours: ["16:00-21:00"], weekend_hours: ["16:00-21:00"] },
  },
  energy_period_comparison: {
    period_3: { kwh_a: 13395.45, kwh_b: 12798.2, charge_a: 2910.9652395000003, charge_b: 2781.1768420000003, kwh_diff: -597.25, in_bill_a: true, in_bill_b: true, applies_on: "every day", charge_diff: -129.78839749999997, weekday_hours: ["00:00-14:00", "23:00-24:00"], weekend_hours: ["00:00-14:00", "23:00-24:00"] },
    period_4: { kwh_a: 2262.45, kwh_b: 3420.7, charge_a: 628.463361, charge_b: 950.202046, kwh_diff: 1158.25, in_bill_a: true, in_bill_b: true, applies_on: "every day", charge_diff: 321.73868500000003, weekday_hours: ["14:00-16:00", "21:00-23:00"], weekend_hours: ["14:00-16:00", "21:00-23:00"] },
    period_5: { kwh_a: 2859.1, kwh_b: 2740.6, charge_a: 1319.817742, charge_b: 1265.115772, kwh_diff: -118.5, in_bill_a: true, in_bill_b: true, applies_on: "every day", charge_diff: -54.701970000000074, weekday_hours: ["16:00-21:00"], weekend_hours: ["16:00-21:00"] },
  },
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const usdPrecise = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
const pct = (n) => `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
const kw = (n) => `${n.toFixed(1)} kW`;
const kwh = (n) => `${n.toLocaleString("en-US", { maximumFractionDigits: 0 })} kWh`;

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// last N years, most recent first — used only as a fallback when no
// availablePeriods list is supplied.
function getYearOptions(count = 5) {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => currentYear - i);
}

// Given availablePeriods = [{ year, month }, ...] (month is 1-12), returns
// the distinct years (most recent first) and the months available for
// whichever year is currently selected. Falls back to an unconstrained
// last-5-years / all-12-months list if no periods are supplied.
function getPeriodOptions(availablePeriods, selectedYear) {
  if (availablePeriods && availablePeriods.length) {
    const years = [...new Set(availablePeriods.map((p) => p.year))].sort((a, b) => b - a);
    const months = availablePeriods
      .filter((p) => p.year === selectedYear)
      .map((p) => p.month)
      .sort((a, b) => a - b);
    return { years, months };
  }
  return { years: getYearOptions(5), months: Array.from({ length: 12 }, (_, i) => i + 1) };
}

// Neutral colours are CSS tokens (index.css) so they follow the dashboard's light / dark mode;
// accent colours stay as hex because they are also used in SVG attributes
const COLORS = {
  ink: "var(--c-bg)",           // page background
  panel: "var(--c-surface)",    // card background
  panelRaised: "var(--c-surface)",
  line: "var(--c-border)",      // card borders / hairlines
  teal: "#2F6FED",       // brand blue — "With BESS" / savings
  coral: "#DC2626",      // "Without BESS" / cost increase
  green: "#16A34A",      // reduction indicator (down-arrow badges)
  amber: "#F59E0B",
  textHi: "var(--c-text)",          // navy headings
  textMid: "var(--c-text-3)",       // secondary text
  textLow: "var(--c-text-faint)",   // captions / muted
};

const componentLabels = {
  fixed_charge: "Fixed Charge",
  energy_charge: "Energy Charge",
  tou_demand_charge: "TOU Demand Charge",
  flat_demand_charge: "Flat Demand Charge",
};

// ---------------------------------------------------------------------------
// Time-of-use period helpers — fully data-driven. Demand and energy periods
// are NOT assumed to line up (different tariffs can carve up the day
// differently, or have a different number of periods on each side), so each
// side is parsed and labeled independently from its own weekday_hours.
// ---------------------------------------------------------------------------
function parseHourRange(str) {
  const [startStr, endStr] = str.split("-");
  const toHours = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h + (m || 0) / 60;
  };
  return { start: toHours(startStr), end: toHours(endStr) };
}

function formatHour(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function formatRanges(ranges) {
  return ranges.map((r) => `${formatHour(r.start)}–${formatHour(r.end)}`).join(", ");
}

// Builds a chronologically-ordered list of periods with a label (On/Mid/Off-
// Peak) and color inferred from each period's effective rate ($ per unit of
// usage), not from hard-coded keys or hours.
function buildPeriodMeta(comparisonObj, usageKey, chargeKey) {
  const entries = Object.entries(comparisonObj || {});
  const meta = entries.map(([key, pdata]) => {
    const ranges = (pdata.weekday_hours || [])
      .map(parseHourRange)
      .filter((r) => Number.isFinite(r.start) && Number.isFinite(r.end));
    const duration = ranges.reduce((sum, r) => sum + (r.end - r.start), 0);
    const minStart = ranges.length ? Math.min(...ranges.map((r) => r.start)) : 0;
    const usage = pdata[usageKey] || 0;
    const rate = usage > 0 ? (pdata[chargeKey] || 0) / usage : 0;
    return { key, data: pdata, ranges, duration, minStart, rate };
  });

  // Rank by effective rate (highest = peak). Ties (e.g. two periods both
  // billed $0) fall back to whichever window is shorter, since peak windows
  // are conventionally the narrower ones.
  const ranked = [...meta].sort((a, b) => b.rate - a.rate || a.duration - b.duration);
  const n = ranked.length;
  ranked.forEach((m, i) => {
    if (n <= 1) m.label = "All Hours";
    else if (i === 0) m.label = "On-Peak";
    else if (i === n - 1) m.label = "Off-Peak";
    else m.label = "Mid-Peak";
    m.color = m.label === "On-Peak" ? COLORS.coral : m.label === "Off-Peak" ? "#93C5FD" : COLORS.amber;
  });

  // Display chronologically (by time of day), independent of rank order.
  return [...meta].sort((a, b) => a.minStart - b.minStart);
}

function TimelineStrip({ periods }) {
  const segments = [];
  periods.forEach((p) => {
    p.ranges.forEach((r) => segments.push({ ...r, color: p.color, label: p.label }));
  });
  segments.sort((a, b) => a.start - b.start);
  if (!segments.length) return null;
  return (
    <div style={{ display: "flex", height: 40, borderRadius: 8, overflow: "hidden" }}>
      {segments.map((seg, i) => (
        <div
          key={i}
          style={{
            width: `${((seg.end - seg.start) / 24) * 100}%`,
            background: seg.color,
            opacity: seg.label === "On-Peak" ? 1 : seg.label === "Off-Peak" ? 0.55 : 0.85,
            borderRight: i < segments.length - 1 ? "2px solid var(--c-surface)" : "none",
          }}
        />
      ))}
    </div>
  );
}

function TimelineLegend({ periods }) {
  const seen = new Set();
  const unique = periods.filter((p) => (seen.has(p.label) ? false : seen.add(p.label)));
  return (
    <div style={{ display: "flex", gap: 18, marginTop: 16, marginBottom: 4, flexWrap: "wrap" }}>
      {unique.map((p) => (
        <div key={p.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: COLORS.textMid }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
          {p.label}
        </div>
      ))}
    </div>
  );
}

function DiffBadge({ value, invert = false, unit = "$" }) {
  const isZero = Math.abs(value) < 0.005;
  const good = isZero ? null : invert ? value > 0 : value < 0;
  const color = isZero ? COLORS.textLow : good ? COLORS.green : COLORS.coral;
  const arrow = isZero ? "—" : value < 0 ? "↓" : "↑";
  const display = unit === "$" ? usdPrecise(Math.abs(value)) : unit === "kw" ? kw(Math.abs(value)) : kwh(Math.abs(value));
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      color, fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
      background: isZero ? "transparent" : `${color}1A`, padding: isZero ? 0 : "2px 8px", borderRadius: 20,
    }}>
      <span>{arrow}</span>
      <span>{isZero ? "No change" : display}</span>
    </span>
  );
}

function ComponentRow({ id, comp, maxVal, labelA, labelB }) {
  const widthA = (comp.value_a / maxVal) * 100;
  const widthB = (comp.value_b / maxVal) * 100;
  return (
    <div style={{ padding: "18px 0", borderBottom: `1px solid ${COLORS.line}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: COLORS.textHi }}>
          {componentLabels[id]}
        </span>
        <DiffBadge value={comp.diff} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 88, fontSize: 11, color: COLORS.textLow, fontFamily: "'Inter', sans-serif" }}>{labelA}</span>
          <div style={{ flex: 1, height: 8, background: COLORS.ink, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${widthA}%`, height: "100%", background: COLORS.coral, borderRadius: 4 }} />
          </div>
          <span style={{ width: 90, textAlign: "right", fontSize: 12.5, color: COLORS.textMid, fontFamily: "'Inter', sans-serif" }}>{usdPrecise(comp.value_a)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 88, fontSize: 11, color: COLORS.textLow, fontFamily: "'Inter', sans-serif" }}>{labelB}</span>
          <div style={{ flex: 1, height: 8, background: COLORS.ink, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${widthB}%`, height: "100%", background: COLORS.teal, borderRadius: 4 }} />
          </div>
          <span style={{ width: 90, textAlign: "right", fontSize: 12.5, color: COLORS.textMid, fontFamily: "'Inter', sans-serif" }}>{usdPrecise(comp.value_b)}</span>
        </div>
      </div>
    </div>
  );
}

function PeriodCard({ meta, type }) {
  const p = meta.data;
  return (
    <div style={{
      background: COLORS.panelRaised, border: `1px solid ${COLORS.line}`, borderRadius: 14,
      padding: "20px 20px 22px", flex: 1, minWidth: 240, boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
        <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: COLORS.textHi }}>{meta.label}</span>
      </div>
      <div style={{ fontSize: 11.5, color: COLORS.textLow, fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>
        {formatRanges(meta.ranges)}
      </div>

      {type === "demand" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
          <Metric label="Peak Demand" a={kw(p.peak_kw_a)} b={kw(p.peak_kw_b)} diff={<DiffBadge value={p.peak_kw_diff} unit="kw" />} />
          <Metric label="Demand Charge" a={usdPrecise(p.charge_a)} b={usdPrecise(p.charge_b)} diff={<DiffBadge value={p.charge_diff} />} />
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
          <Metric label="Energy Used" a={kwh(p.kwh_a)} b={kwh(p.kwh_b)} diff={<DiffBadge value={p.kwh_diff} invert unit="kwh" />} />
          <Metric label="Energy Charge" a={usdPrecise(p.charge_a)} b={usdPrecise(p.charge_b)} diff={<DiffBadge value={p.charge_diff} />} />
        </div>
      )}
    </div>
  );
}

function Metric({ label, a, b, diff }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, letterSpacing: "0.04em", textTransform: "uppercase", color: COLORS.textLow, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: COLORS.textMid, marginBottom: 4 }}>
        {a} <span style={{ color: COLORS.textLow }}>→</span> <span style={{ color: COLORS.textHi }}>{b}</span>
      </div>
      {diff}
    </div>
  );
}

function RefreshButton({ onRefresh, isRefreshing }) {
  return (
    <button
      onClick={onRefresh}
      disabled={isRefreshing}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        background: isRefreshing ? "var(--c-accent-bg)" : "var(--c-surface)",
        color: COLORS.teal, border: `1px solid ${isRefreshing ? "#C7D9FB" : COLORS.line}`,
        borderRadius: 9, padding: "9px 14px", fontFamily: "'Inter', sans-serif",
        fontSize: 13.5, fontWeight: 600, cursor: isRefreshing ? "default" : "pointer",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)", whiteSpace: "nowrap",
      }}
    >
      <svg
        width="15" height="15" viewBox="0 0 24 24" fill="none"
        style={{ animation: isRefreshing ? "bess-spin 0.8s linear infinite" : "none", transformOrigin: "center" }}
      >
        <path
          d="M20 11A8 8 0 1 0 19.5 15" stroke={COLORS.teal} strokeWidth="2.2"
          strokeLinecap="round" fill="none"
        />
        <path d="M20 5v6h-6" stroke={COLORS.teal} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
      {isRefreshing ? "Refreshing…" : "Refresh"}
    </button>
  );
}

function DownloadPdfButton({ onDownload, isGenerating, disabled }) {
  return (
    <button
      onClick={onDownload}
      disabled={disabled || isGenerating}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        background: isGenerating ? "var(--c-accent-bg)" : "var(--c-surface)",
        color: COLORS.teal, border: `1px solid ${isGenerating ? "#C7D9FB" : COLORS.line}`,
        borderRadius: 9, padding: "9px 14px", fontFamily: "'Inter', sans-serif",
        fontSize: 13.5, fontWeight: 600, cursor: disabled || isGenerating ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)", whiteSpace: "nowrap",
      }}
    >
      {isGenerating ? (
        <svg
          width="15" height="15" viewBox="0 0 24 24" fill="none"
          style={{ animation: "bess-spin 0.8s linear infinite", transformOrigin: "center" }}
        >
          <path d="M20 11A8 8 0 1 0 19.5 15" stroke={COLORS.teal} strokeWidth="2.2" strokeLinecap="round" fill="none" />
          <path d="M20 5v6h-6" stroke={COLORS.teal} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M12 3v11.5M8 11l4 4 4-4M4.5 19h15" stroke={COLORS.teal} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      )}
      {isGenerating ? "Preparing PDF…" : "Download PDF"}
    </button>
  );
}

// options: array of { value, label } — value can be a number (e.g. month 1-12,
// year 2026); onChange is always called with the matching option's original
// (typed) value, not the raw string HTML selects hand back.
function Dropdown({ value, onChange, options, minWidth }) {
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <select
        value={value}
        onChange={(e) => {
          const raw = e.target.value;
          const matched = options.find((o) => String(o.value) === raw);
          onChange(matched ? matched.value : raw);
        }}
        style={{
          appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
          background: "var(--c-surface)", color: COLORS.textHi, border: `1px solid ${COLORS.line}`,
          borderRadius: 9, padding: "9px 34px 9px 14px", fontFamily: "'Inter', sans-serif",
          fontSize: 13.5, fontWeight: 600, cursor: "pointer", minWidth,
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <svg
        width="13" height="13" viewBox="0 0 24 24" fill="none"
        style={{ position: "absolute", right: 12, top: "50%", marginTop: -6.5, pointerEvents: "none" }}
      >
        <path d="M6 9l6 6 6-6" style={{ stroke: COLORS.textMid }} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

function SkeletonBlock({ height = 16, width = "100%", radius = 6, style = {} }) {
  return (
    <div
      style={{
        height, width, borderRadius: radius, background: "var(--c-muted)",
        animation: "bess-pulse 1.4s ease-in-out infinite", ...style,
      }}
    />
  );
}

function LoadingSkeleton() {
  return (
    <div>
      <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 18, padding: "32px 28px", marginBottom: 28, boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
        <SkeletonBlock height={12} width={220} style={{ marginBottom: 14 }} />
        <SkeletonBlock height={44} width={280} style={{ marginBottom: 24 }} />
        <SkeletonBlock height={34} width="100%" radius={8} />
      </div>
      <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 18, padding: "22px", marginBottom: 28, boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ marginBottom: i < 3 ? 18 : 0 }}>
            <SkeletonBlock height={12} width={140} style={{ marginBottom: 10 }} />
            <SkeletonBlock height={8} width="100%" style={{ marginBottom: 6 }} />
            <SkeletonBlock height={8} width="100%" />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ flex: 1, minWidth: 240, background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 14, padding: 20, boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
            <SkeletonBlock height={14} width={100} style={{ marginBottom: 16 }} />
            <SkeletonBlock height={38} width="100%" style={{ marginBottom: 10 }} />
            <SkeletonBlock height={38} width="100%" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorState({ onRetry, isRetrying, title, message }) {
  return (
    <div style={{
      background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 18,
      padding: "48px 28px", textAlign: "center", boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.textHi, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: COLORS.textMid, marginBottom: 20 }}>
        {message}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <RefreshButton onRefresh={onRetry} isRefreshing={isRetrying} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
// data              object | null        — the bill-comparison payload (see sampleData shape). null/undefined while loading or on error.
// isLoading         bool                 — true while the parent is fetching for the first time (shows skeleton).
// isRefreshing      bool                 — true while the parent is re-fetching (spins the refresh icon).
// error             string | null       — set by the parent if the fetch failed (shows the error state).
// selectedMonth     number               — 1-12. Parent owns this value.
// selectedYear      number               — e.g. 2026. Parent owns this value.
// availablePeriods  { year, month }[]   — limits the dropdowns to only these year/month combos,
//                                          e.g. [{ year: 2026, month: 5 }, { year: 2026, month: 6 }].
//                                          If omitted, falls back to an unconstrained last-5-years / all-12-months list.
// onMonthChange     (month) => void      — called with the numeric month (1-12) the user picked.
// onYearChange      (year) => void       — called with the numeric year the user picked.
// onRefresh         () => void           — called when the user clicks Refresh.
// ---------------------------------------------------------------------------
export default function BessBillComparison({
  data = null,
  isLoading = false,
  isRefreshing = false,
  error = null,
  selectedMonth = new Date().getMonth() + 1,
  selectedYear = new Date().getFullYear(),
  availablePeriods = null,
  onMonthChange = () => {},
  onYearChange = () => {},
  onRefresh = () => {},
}) {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const reportRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const periodLabel = `${MONTH_NAMES[selectedMonth - 1] || ""} ${selectedYear}`.trim();

  const handleDownloadPdf = async () => {
    if (!reportRef.current || isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: "#F4F6FA",
        scale: 2,
        useCORS: true,
        // Always export the light version, even when the dashboard is in dark mode
        onclone: (clonedDoc) => clonedDoc.documentElement.setAttribute("data-theme", "light"),
      });

      const marginPt = 36; // 0.5" on all sides — tweak to taste
      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const usableWidth = pdfWidth - marginPt * 2;
      const usableHeight = pdfHeight - marginPt * 2;
      const bg = hexToRgb(COLORS.ink);
      const textMid = hexToRgb(COLORS.textMid);

      // px-per-pt scale, based on fitting the canvas's width into the
      // margined usable width. Slicing (rather than shifting one big image
      // with a negative offset) means each page only ever draws the exact
      // content that belongs on it, so nothing bleeds into the margin band.
      const scale = canvas.width / usableWidth;
      const pageHeightPx = Math.floor(usableHeight * scale);
      const totalPages = Math.max(1, Math.ceil(canvas.height / pageHeightPx));

      let renderedPx = 0;
      let pageIndex = 1;

      while (renderedPx < canvas.height) {
        const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx);

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeightPx;
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0, renderedPx, canvas.width, sliceHeightPx,
          0, 0, canvas.width, sliceHeightPx
        );

        const sliceDataUrl = pageCanvas.toDataURL("image/png");
        const sliceHeightPt = sliceHeightPx / scale;

        if (pageIndex > 1) pdf.addPage();
        pdf.setFillColor(bg.r, bg.g, bg.b);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
        pdf.addImage(sliceDataUrl, "PNG", marginPt, marginPt, usableWidth, sliceHeightPt);

        // Running header/footer, drawn on every page so the billing period
        // stays visible even past page 1 (the on-screen dropdowns showing
        // the period are deliberately excluded from the capture itself).
        pdf.setFontSize(9);
        pdf.setTextColor(textMid.r, textMid.g, textMid.b);
        pdf.text(`Utility Bill Comparison — ${periodLabel}`, marginPt, 22);
        pdf.text(`Page ${pageIndex} of ${totalPages}`, pdfWidth - marginPt, pdfHeight - 14, { align: "right" });

        renderedPx += sliceHeightPx;
        pageIndex += 1;
      }

      const monthLabel = String(selectedMonth).padStart(2, "0");
      pdf.save(`utility-bill-comparison-${selectedYear}-${monthLabel}.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF", err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const s = data?.summary ?? null;
  const pctA = s ? (s.total_a / (s.total_a + s.total_b)) * 100 : 0;
  const pctB = 100 - pctA;
  const maxComponentVal = data?.components
    ? Math.max(
        ...Object.entries(data.components)
          .filter(([k]) => k !== "total_estimated_bill")
          .flatMap(([, v]) => [v.value_a, v.value_b])
      )
    : 1;

  const demandPeriods = data ? buildPeriodMeta(data.demand_period_comparison, "peak_kw_a", "charge_a") : [];
  const energyPeriods = data ? buildPeriodMeta(data.energy_period_comparison, "kwh_a", "charge_a") : [];

  const { years, months } = getPeriodOptions(availablePeriods, selectedYear);
  const yearDropdownOptions = years.map((y) => ({ value: y, label: String(y) }));
  const monthDropdownOptions = months.map((m) => ({ value: m, label: MONTH_NAMES[m - 1] }));

  return (
    <div style={{
      background: COLORS.ink, minHeight: "100vh", color: COLORS.textHi,
      fontFamily: "'Inter', sans-serif", padding: "40px 20px 64px",
    }}>
      <div ref={reportRef} style={{ maxWidth: 880, margin: "0 auto" }}>
        <style>{`
          @keyframes bess-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes bess-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
        `}</style>

        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 36 }}>
          <div>
            <div style={{ fontSize: 11.5, letterSpacing: "0.12em", color: COLORS.teal, fontWeight: 600, marginBottom: 8 }}>
              BATTERY STORAGE IMPACT
            </div>
            <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(24px, 4vw, 34px)", margin: 0, color: COLORS.textHi }}>
              Utility Bill Comparison
            </h1>
            <div style={{ fontSize: 13, color: COLORS.textMid, fontWeight: 600, marginTop: 8 }}>{periodLabel}</div>
            {data?.same_billing_period && (
              <div style={{ fontSize: 12.5, color: COLORS.textLow, marginTop: 4 }}>Same billing period · {data.label_a} vs {data.label_b}</div>
            )}
          </div>
          <div data-html2canvas-ignore="true" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Dropdown value={selectedMonth} onChange={onMonthChange} options={monthDropdownOptions} minWidth={130} />
            <Dropdown value={selectedYear} onChange={onYearChange} options={yearDropdownOptions} minWidth={90} />
            <RefreshButton onRefresh={onRefresh} isRefreshing={isRefreshing || isLoading} />
            <DownloadPdfButton
              onDownload={handleDownloadPdf}
              isGenerating={isGeneratingPdf}
              disabled={isLoading || !data}
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <ErrorState
            onRetry={onRefresh}
            isRetrying={isRefreshing}
            title="Couldn't load this bill comparison"
            message={error}
          />
        ) : !data ? (
          <ErrorState
            onRetry={onRefresh}
            isRetrying={isRefreshing}
            title="No data to show"
            message="No billing comparison is available for this period yet."
          />
        ) : (
          <>
        {/* hero */}
        <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 18, padding: "32px 28px", marginBottom: 28, boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
          <div style={{ fontSize: 12.5, color: COLORS.textMid, marginBottom: 6 }}>Estimated savings with battery storage</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(36px, 6vw, 52px)", color: COLORS.teal, lineHeight: 1 }}>
              {usd(Math.abs(s.total_diff))}
            </span>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: COLORS.textMid }}>
              {pct(s.pct_change)} lower
            </span>
          </div>

          <div style={{ display: "flex", height: 34, borderRadius: 8, overflow: "hidden", marginTop: 26, marginBottom: 10 }}>
            <div style={{ width: `${pctA}%`, background: COLORS.coral, display: "flex", alignItems: "center", paddingLeft: 12 }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12.5, fontWeight: 600, color: "#FFFFFF" }}>{usdPrecise(s.total_a)}</span>
            </div>
            <div style={{ width: `${pctB}%`, background: COLORS.teal, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 12 }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12.5, fontWeight: 600, color: "#FFFFFF" }}>{usdPrecise(s.total_b)}</span>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: COLORS.textLow, fontFamily: "'Inter', sans-serif" }}>
            <span>{data.label_a}</span>
            <span>{data.label_b}</span>
          </div>
        </div>

        {/* component breakdown */}
        <div style={{ marginBottom: 36 }}>
          <SectionHeading eyebrow="LINE ITEMS" title="Where the bill changes" />
          <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 18, padding: "6px 22px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
            {Object.entries(data.components)
              .filter(([k]) => k !== "total_estimated_bill")
              .map(([id, comp]) => (
                <ComponentRow key={id} id={id} comp={comp} maxVal={maxComponentVal} labelA={data.label_a} labelB={data.label_b} />
              ))}
          </div>
        </div>

        {/* Demand charges by rate period — signature element, fully data-driven */}
        {demandPeriods.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <SectionHeading eyebrow="LOAD PROFILE" title="Demand charges by rate period" />

            <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 18, padding: "24px 22px 18px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
              <TimelineStrip periods={demandPeriods} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10.5, color: COLORS.textLow, fontFamily: "'Inter', sans-serif" }}>
                {["00:00", "06:00", "12:00", "18:00", "24:00"].map((t) => <span key={t}>{t}</span>)}
              </div>
              <TimelineLegend periods={demandPeriods} />
            </div>

            <div style={{ display: "flex", gap: 14, marginTop: 18, flexWrap: "wrap" }}>
              {demandPeriods.map((meta) => (
                <PeriodCard key={meta.key} meta={meta} type="demand" />
              ))}
            </div>
          </div>
        )}

        {/* Energy usage by rate period — parsed independently, may not align with demand periods above */}
        {energyPeriods.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <SectionHeading eyebrow="LOAD PROFILE" title="Energy usage by rate period" />

            <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 18, padding: "24px 22px 18px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
              <TimelineStrip periods={energyPeriods} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10.5, color: COLORS.textLow, fontFamily: "'Inter', sans-serif" }}>
                {["00:00", "06:00", "12:00", "18:00", "24:00"].map((t) => <span key={t}>{t}</span>)}
              </div>
              <TimelineLegend periods={energyPeriods} />
            </div>

            <div style={{ display: "flex", gap: 14, marginTop: 18, flexWrap: "wrap" }}>
              {energyPeriods.map((meta) => (
                <PeriodCard key={meta.key} meta={meta} type="energy" />
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: COLORS.textLow, marginTop: 30, textAlign: "center" }}>
          Estimates based on modeled demand and energy usage for the selected billing period.
        </div>
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.1em", color: COLORS.textLow, fontWeight: 600, marginBottom: 4 }}>{eyebrow}</div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 19, fontWeight: 600, color: COLORS.textHi }}>{title}</div>
    </div>
  );
}